import Ticker from "./Ticker";
import Scene from "./Scene";
import type { SystemConstructor } from "../core/System";
import System from "../core/System";
import Component from "../core/Component";
import { setSystemObserver, initObserver } from "../core/observer";
import EventEmitter from "eventemitter3";
import {
  postParentPluginInitSuccess,
  postParentGameReady,
  postParentGameState,
  parseSetPlayingMessage,
} from "./bootstrapMessages";
import {
  isAllowedMessageOrigin,
  mergeAllowedMessageOrigins,
} from "./messageOrigin";

function systemClassName(ctor: Function): string {
  if ("systemName" in ctor) {
    const sn = (ctor as { systemName?: string }).systemName;
    if (typeof sn === "string") return sn;
  }
  return "UnknownSystem";
}

function systemPackageMeta(ctor: Function): {
  packageName?: string;
  packageVersion?: string;
} {
  const meta = ctor as SystemConstructor;
  return {
    packageName:
      typeof meta.packageName === "string" ? meta.packageName : undefined,
    packageVersion:
      typeof meta.packageVersion === "string" ? meta.packageVersion : undefined,
  };
}

function componentClassName(ctor: Function): string {
  if ("componentName" in ctor) {
    const cn = (ctor as { componentName?: string }).componentName;
    if (typeof cn === "string") return cn;
  }
  return "UnknownComponent";
}

/** Plugin registration shape */
export interface PluginStruct {
  Components?: (typeof Component)[];
  Systems?: (typeof System)[];
}

export interface GameParams {
  /** isn't game will auto start */
  autoStart?: boolean;

  /** fps for this game */
  frameRate?: number;

  /** systems in this game */
  systems?: System[];

  /** whether or not need to create scene */
  needScene?: boolean;

  /**
   * `postMessage` targetOrigin when notifying `window.parent` of lifecycle events
   * (`combos-game:plugin-init-success`, `combos-game:ready`, `combos-game:state-changed`).
   * Default `'*'`. Only used when `window.parent` exists and differs from `window`.
   */
  pluginInitNotifyTargetOrigin?: string;

  /**
   * Inbound `postMessage` origins allowed to send host commands (`combos-game:set-playing`),
   * merged with {@link DEFAULT_ALLOWED_MESSAGE_HOST_SUFFIXES}. Each entry is a host suffix
   * (e.g. `localhost`) or a full origin. Pass `['*']` to accept any origin (dev only).
   */
  allowedMessageOrigins?: string[];

  /**
   * Called after async system bootstrap finishes (constructor path with non-empty `systems`),
   * i.e. after all `addSystem`/`init` work and optional `loadScene` / `start`.
   * Second argument is set if bootstrap threw (e.g. async `init` rejection).
   */
  onSystemsBootstrapComplete?: (game: Game, error?: unknown) => void;
}

export enum LOAD_SCENE_MODE {
  SINGLE = "SINGLE",
  MULTI_CANVAS = "MULTI_CANVAS",
}

interface LoadSceneParams {
  scene: Scene;
  mode?: LOAD_SCENE_MODE;
  params?: {
    width?: number;
    height?: number;
    canvas?: HTMLCanvasElement;
    renderType?: number;
    autoStart?: boolean;
    sharedTicker?: boolean;
    sharedLoader?: boolean;
    transparent?: boolean;
    antialias?: boolean;
    preserveDrawingBuffer?: boolean;
    resolution?: number;
    backgroundColor?: number;
    clearBeforeRender?: boolean;
    roundPixels?: boolean;
    forceFXAA?: boolean;
    legacy?: boolean;
    autoResize?: boolean;
    powerPreference?: "high-performance";
  };
}

const triggerStart = (obj: System | Component) => {
  if (!(obj instanceof System) && !(obj instanceof Component)) return;
  if (obj.started) return;
  obj.started = true;

  try {
    obj.start && obj.start();
  } catch (e) {
    if (obj instanceof Component) {
      console.error(`${componentClassName(obj.constructor)} start error`, e);
    } else {
      console.error(`${systemClassName(obj.constructor)} start error`, e);
    }
  }
};

const getAllGameObjects = (game: Game) => {
  const mainSceneGameObjects = game?.scene?.gameObjects || [];

  const gameObjectsArray = game?.multiScenes.map(
    ({ gameObjects }) => gameObjects,
  );
  let otherSceneGameObjects = [];
  for (const gameObjects of gameObjectsArray) {
    otherSceneGameObjects = [...otherSceneGameObjects, ...gameObjects];
  }
  return [...mainSceneGameObjects, ...otherSceneGameObjects];
};

const gameObjectLoop = (e, gameObjects = []) => {
  for (const gameObject of gameObjects) {
    for (const component of gameObject.components) {
      try {
        triggerStart(component);
        component.update && component.update(e);
      } catch (e) {
        console.error(
          `gameObject: ${gameObject.name} ${component.name} update error`,
          e,
        );
      }
    }
  }
  for (const gameObject of gameObjects) {
    for (const component of gameObject.components) {
      try {
        component.lateUpdate && component.lateUpdate(e);
      } catch (e) {
        console.error(
          `gameObject: ${gameObject.name} ${component.name} lateUpdate error`,
          e,
        );
      }
    }
  }
};

const gameObjectResume = (gameObjects) => {
  for (const gameObject of gameObjects) {
    for (const component of gameObject.components) {
      try {
        component.onResume && component.onResume();
      } catch (e) {
        console.error(
          `gameObject: ${gameObject.name}, ${component.name}, onResume error`,
          e,
        );
      }
    }
  }
};

const gameObjectPause = (gameObjects) => {
  for (const gameObject of gameObjects) {
    for (const component of gameObject.components) {
      try {
        component.onPause && component.onPause();
      } catch (e) {
        console.error(
          `gameObject: ${gameObject.name}, ${component.name}, onPause error`,
          e,
        );
      }
    }
  }
};

class Game extends EventEmitter {
  _scene: Scene;
  canvas: HTMLCanvasElement;

  /**
   * State of game
   * @defaultValue false
   */
  playing: boolean = false;
  started: boolean = false;
  multiScenes: Scene[] = [];

  /**
   * Ticker
   */
  ticker: Ticker;

  /** Systems alled to this game */
  systems: System[] = [];

  /**
   * Passed to `postMessage` when reporting lifecycle events to `window.parent`
   * (`plugin-init-success`, `ready`, `state-changed`).
   */
  pluginInitNotifyTargetOrigin: string = "*";

  /** Inbound `postMessage` origins allowed to send host commands. */
  private allowedMessageOrigins: string[] = mergeAllowedMessageOrigins();

  /** Bound inbound `message` handler; retained so it can be removed on `destroy`. */
  private readonly onHostMessage = (e: MessageEvent) => {
    if (!isAllowedMessageOrigin(e.origin, this.allowedMessageOrigins)) return;
    const playing = parseSetPlayingMessage(e.data);
    if (playing === null) return;
    this.setPlaying(playing);
  };

  constructor({
    systems,
    frameRate = 60,
    autoStart = true,
    needScene = true,
    onSystemsBootstrapComplete,
    pluginInitNotifyTargetOrigin = "*",
    allowedMessageOrigins,
  }: GameParams = {}) {
    super();
    this.pluginInitNotifyTargetOrigin = pluginInitNotifyTargetOrigin;
    this.allowedMessageOrigins = mergeAllowedMessageOrigins(allowedMessageOrigins);
    this.ticker = new Ticker({ autoStart: false, frameRate });
    this.initTicker();

    if (typeof window !== "undefined") {
      window.addEventListener("message", this.onHostMessage);
    }

    if (systems && systems.length) {
      const systemsToAdd = [...systems];
      void this.bootstrapSystemsAndMaybeStart(
        systemsToAdd,
        needScene,
        autoStart,
        onSystemsBootstrapComplete,
      );
    } else {
      onSystemsBootstrapComplete?.(this);
      this.notifyReady();
    }
  }

  /**
   * Host command bridge: `true` cold-starts on first play (`start`), otherwise
   * `resume`s; `false` pauses. Mirrors what a parent frame drives via
   * `postMessage({ type: 'combos-game:set-playing', playing })`.
   */
  setPlaying(playing: boolean) {
    if (playing) {
      if (this.started) {
        this.resume();
      } else {
        this.start();
      }
    } else {
      this.pause();
    }
  }

  private notifyReady(error?: unknown) {
    postParentGameReady(error, this.pluginInitNotifyTargetOrigin);
  }

  private notifyStateChanged() {
    postParentGameState(
      { playing: this.playing, started: this.started },
      this.pluginInitNotifyTargetOrigin,
    );
  }

  /** When `systems` is passed in the constructor, run async `init` for each before `loadScene` / `start`. */
  private async bootstrapSystemsAndMaybeStart(
    systemsToAdd: System[],
    needScene: boolean,
    autoStart: boolean,
    onComplete?: (game: Game, error?: unknown) => void,
  ): Promise<void> {
    let error: unknown;
    try {
      for (const system of systemsToAdd) {
        await this.addSystem(system);
      }
      if (needScene) {
        this.loadScene({ scene: new Scene("scene") });
      }
      if (autoStart) {
        this.start();
      }
    } catch (e) {
      error = e;
      console.error("Game bootstrap failed", e);
    } finally {
      onComplete?.(this, error);
      this.notifyReady(error);
    }
  }

  /**
   * Get scene on this game
   */
  get scene() {
    return this._scene;
  }

  set scene(scene: Scene) {
    this._scene = scene;
  }

  get gameObjects() {
    return getAllGameObjects(this);
  }

  addSystem<T extends System>(S: T): Promise<T | undefined>;
  addSystem<T extends System>(
    S: SystemConstructor<T>,
    obj?: ConstructorParameters<SystemConstructor<T>>,
  ): Promise<T | undefined>;

  /**
   * Add system
   * @param S - system instance or system Class
   * @typeParam T - system which extends base `System` class
   * @typeparam U - type of system class
   */
  async addSystem<T extends System>(
    S: T | SystemConstructor<T>,
    obj?: ConstructorParameters<SystemConstructor<T>>,
  ): Promise<T | undefined> {
    let system;
    if (S instanceof Function) {
      system = new S(obj);
    } else if (S instanceof System) {
      system = S;
    } else {
      console.warn("can only add System");
      return;
    }

    const hasTheSystem = this.systems.find((item) => {
      return item.constructor === system.constructor;
    });
    if (hasTheSystem) {
      console.warn(
        `${systemClassName(system.constructor)} System has been added`,
      );
      return;
    }

    system.game = this;
    if (system.init) {
      await Promise.resolve(system.init(system.__systemDefaultParams));
    }

    postParentPluginInitSuccess(
      {
        systemName: systemClassName(system.constructor),
        ...systemPackageMeta(system.constructor),
      },
      this.pluginInitNotifyTargetOrigin,
    );

    setSystemObserver(system, system.constructor);
    initObserver(system.constructor);

    try {
      system.awake && system.awake();
    } catch (e) {
      console.error(`${systemClassName(system.constructor)} awake error`, e);
    }

    this.systems.push(system);
    return system;
  }

  /**
   * Remove system from this game
   * @param system - one of system instance / system Class or system name
   */
  removeSystem<S extends System>(system: S | SystemConstructor<S> | string) {
    if (!system) return;

    let index = -1;
    if (typeof system === "string") {
      index = this.systems.findIndex((s) => s.name === system);
    } else if (system instanceof Function) {
      index = this.systems.findIndex((s) => s.constructor === system);
    } else if (system instanceof System) {
      index = this.systems.findIndex((s) => s === system);
    }

    if (index > -1) {
      this.systems[index].destroy && this.systems[index].destroy();
      this.systems.splice(index, 1);
    }
  }

  /**
   * Get system
   * @param S - system class or system name
   * @returns system instance
   */
  getSystem<T extends System>(S: SystemConstructor<T> | string): T {
    return this.systems.find((system) => {
      if (typeof S === "string") {
        return system.name === S;
      } else {
        return system instanceof S;
      }
    }) as T;
  }

  /** Pause game */
  pause() {
    if (!this.playing) return;
    this.playing = false;
    this.ticker.pause();
    this.triggerPause();
    this.notifyStateChanged();
  }

  /** Start game */
  start() {
    if (this.playing) return;
    this.playing = true;
    this.started = true;
    this.ticker.start();
    this.notifyStateChanged();
  }

  /** Resume game */
  resume() {
    if (this.playing) return;
    this.playing = true;
    this.ticker.start();
    this.triggerResume();
    this.notifyStateChanged();
  }

  /**
   * add main render method to ticker
   * @remarks
   * the method added to ticker will called in each requestAnimationFrame,
   * 1. call update method on all gameObject
   * 2. call lastUpdate method on all gameObject
   * 3. call update method on all system
   * 4. call lastUpdate method on all system
   */
  initTicker() {
    this.ticker.add((e) => {
      this.scene && gameObjectLoop(e, this.gameObjects);
      for (const system of this.systems) {
        try {
          triggerStart(system);
          system.update && system.update(e);
        } catch (e) {
          console.error(
            `${systemClassName(system.constructor)} update error`,
            e,
          );
        }
      }
      for (const system of this.systems) {
        try {
          system.lateUpdate && system.lateUpdate(e);
        } catch (e) {
          console.error(
            `${systemClassName(system.constructor)} lateUpdate error`,
            e,
          );
        }
      }
    });
  }

  /** Call onResume method on all gameObject's, and then call onResume method on all system */
  triggerResume() {
    gameObjectResume(this.gameObjects);
    for (const system of this.systems) {
      try {
        system.onResume && system.onResume();
      } catch (e) {
        console.error(
          `${systemClassName(system.constructor)}, onResume error`,
          e,
        );
      }
    }
  }

  /** Call onPause method on all gameObject */
  triggerPause() {
    gameObjectPause(this.gameObjects);

    for (const system of this.systems) {
      try {
        system.onPause && system.onPause();
      } catch (e) {
        console.error(
          `${systemClassName(system.constructor)}, onPause error`,
          e,
        );
      }
    }
  }

  // TODO: call system destroy method
  /** remove all system on this game */
  destroySystems() {
    for (const system of [...this.systems]) {
      this.removeSystem(system);
    }
    this.systems.length = 0;
  }

  /** Destroy game instance */
  destroy() {
    if (typeof window !== "undefined") {
      window.removeEventListener("message", this.onHostMessage);
    }
    this.removeAllListeners();
    this.pause();
    this.scene.destroy();
    this.destroySystems();
    this.ticker = null;
    this.scene = null;
    this.canvas = null;
    this.multiScenes = null;
  }

  loadScene({
    scene,
    mode = LOAD_SCENE_MODE.SINGLE,
    params = {},
  }: LoadSceneParams) {
    if (!scene) {
      return;
    }
    scene.game = this;
    switch (mode) {
      case LOAD_SCENE_MODE.SINGLE:
        this.scene = scene;
        break;

      case LOAD_SCENE_MODE.MULTI_CANVAS:
        this.multiScenes.push(scene);
        break;
    }
    this.emit("sceneChanged", { scene, mode, params });
  }
}

export default Game;
